import React, { useState, useEffect, useMemo } from 'react';
import { 
    User, Calendar, Mail, Phone, DollarSign, Layers, 
    Clock, CheckCircle, AlertCircle, Search, 
    Printer, Plus, List, Sparkles, CheckCheck, RotateCw,
    CalendarPlus, History, Dumbbell
} from 'lucide-react';
import api from '../services/api';
import ReceiptModal from '../components/ReceiptModal';
import ClientBadgeModal from '../components/ClientBadgeModal';
import ReabonnementModal from '../components/ReabonnementModal';
import ProlongerModal from '../components/ProlongerModal';
import EvolutionHistoryModal from '../components/EvolutionHistoryModal';
import TableActionMenu from '../components/TableActionMenu';
import { BadgeCheck } from 'lucide-react';

interface ForfaitSeance {
    id: number;
    name: string;
    price: number | string;
    registration_fee?: number | string;
    description?: string;
    with_coach?: boolean | number;
    active: boolean | number;
}

interface ClientOption {
    id: number;
    first_name: string;
    last_name: string;
    phone: string;
    email?: string;
    gender?: string;
    date_of_birth?: string;
}

const PAYMENT_METHODS = [
    { value: 'especes', label: 'Espèces' },
    { value: 'wave', label: 'Wave' },
    { value: 'orange_money', label: 'Orange Money' },
    { value: 'mtn_money', label: 'MTN Money' },
    { value: 'moov_money', label: 'Moov Money' },
    { value: 'carte_bancaire', label: 'Carte Bancaire' },
    { value: 'virement', label: 'Virement' },
];

const getCurrentTimeStr = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const Seances = () => {
    // Mode tabs: 'ajout' (default) or 'liste'
    const [activeTab, setActiveTab] = useState<'ajout' | 'liste'>('ajout');

    const handleSwitchTab = (tab: 'ajout' | 'liste') => {
        setActiveTab(tab);
        if (tab === 'liste') {
            fetchSeances();
        }
    };

    // Forfaits list
    const [forfaits, setForfaits] = useState<ForfaitSeance[]>([]);

    // Clients list for autocomplete helper
    const [clients, setClients] = useState<ClientOption[]>([]);
    const [filteredClients, setFilteredClients] = useState<ClientOption[]>([]);
    const [showClientSuggestions, setShowClientSuggestions] = useState(false);

    // Form data exactly matching image 2
    const [formData, setFormData] = useState({
        last_name: '',
        first_name: '',
        gender: '',
        date_of_birth: '',
        email: '',
        phone: '',
        payment_method: '',
        forfait_id: '',
        coach_id: '',
        session_date: new Date().toISOString().split('T')[0],
        session_time: getCurrentTimeStr(),
        include_inscription: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Coaches list
    const [coaches, setCoaches] = useState<any[]>([]);

    // Receipt Modal state
    const [receiptData, setReceiptData] = useState<any>(null);
    const [isReceiptOpen, setIsReceiptOpen] = useState(false);
    const [badgeClient, setBadgeClient] = useState<any>(null);
    const [reaboClient, setReaboClient] = useState<any>(null);
    const [isReaboOpen, setIsReaboOpen] = useState(false);
    const [prolongingSeance, setProlongingSeance] = useState<any | null>(null);
    const [evolutionSeance, setEvolutionSeance] = useState<any | null>(null);

    // Sessions list state
    const [seances, setSeances] = useState<any[]>([]);
    const [loadingSeances, setLoadingSeances] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'valide' | 'consomme' | 'annule'>('all');

    // Fetch coaches
    const fetchCoaches = async () => {
        try {
            const res = await api.get('/coaches');
            setCoaches(res.data || []);
        } catch (error) {
            console.error('Erreur chargement coachs:', error);
        }
    };

    // Fetch active session forfaits
    const fetchForfaits = async () => {
        try {
            const res = await api.get('/forfaits/seances?active_only=true');
            setForfaits(res.data);
            if (res.data.length > 0 && !formData.forfait_id) {
                setFormData(prev => ({ ...prev, forfait_id: String(res.data[0].id) }));
            }
        } catch (error) {
            console.error('Erreur chargement forfaits séances:', error);
        }
    };

    // Fetch clients for auto-completion
    const fetchClients = async () => {
        try {
            const res = await api.get('/clients');
            setClients(res.data);
        } catch (error) {
            console.error('Erreur chargement clients:', error);
        }
    };

    // Fetch existing sessions
    const fetchSeances = async () => {
        try {
            setLoadingSeances(true);
            const res = await api.get('/seances');
            setSeances(res.data);
        } catch (error) {
            console.error('Erreur chargement séances:', error);
        } finally {
            setLoadingSeances(false);
        }
    };

    useEffect(() => {
        fetchForfaits();
        fetchClients();
        fetchCoaches();
        fetchSeances();
    }, []);

    // Selected forfait details
    const selectedForfait = useMemo(() => {
        return forfaits.find(f => String(f.id) === String(formData.forfait_id));
    }, [forfaits, formData.forfait_id]);

    // Total price
    const totalPrice = useMemo(() => {
        if (!selectedForfait) return 0;
        const base = Number(selectedForfait.price);
        const inscription = Number(selectedForfait.registration_fee || 0);
        return base + inscription;
    }, [selectedForfait]);
    const handlePhoneChange = (val: string) => {
        setFormData(prev => ({ ...prev, phone: val }));
        if (val.trim().length >= 2) {
            const matches = clients.filter(c => 
                (c.phone && c.phone.includes(val.trim())) ||
                (`${c.last_name} ${c.first_name}`.toLowerCase().includes(val.toLowerCase()))
            );
            setFilteredClients(matches.slice(0, 5));
            setShowClientSuggestions(matches.length > 0);
        } else {
            setShowClientSuggestions(false);
        }
    };

    const handleSelectClient = (c: ClientOption) => {
        setFormData(prev => ({
            ...prev,
            last_name: c.last_name || prev.last_name,
            first_name: c.first_name || prev.first_name,
            phone: c.phone || prev.phone,
            email: c.email || prev.email,
            gender: c.gender || prev.gender,
            date_of_birth: c.date_of_birth ? c.date_of_birth.substring(0, 10) : prev.date_of_birth
        }));
        setShowClientSuggestions(false);
    };

    const resetForm = () => {
        setFormData({
            last_name: '',
            first_name: '',
            gender: '',
            date_of_birth: '',
            email: '',
            phone: '',
            payment_method: '',
            forfait_id: forfaits.length > 0 ? String(forfaits[0].id) : '',
            coach_id: '',
            session_date: new Date().toISOString().split('T')[0],
            session_time: getCurrentTimeStr(),
            include_inscription: false
        });
        setShowClientSuggestions(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFeedback(null);

        // Validation
        if (!formData.last_name.trim()) {
            setFeedback({ type: 'error', message: 'Veuillez saisir le nom du client' });
            return;
        }
        if (!formData.first_name.trim()) {
            setFeedback({ type: 'error', message: 'Veuillez saisir le(s) prénom(s) du client' });
            return;
        }
        if (!formData.gender) {
            setFeedback({ type: 'error', message: 'Veuillez sélectionner le genre' });
            return;
        }
        if (!formData.phone.trim()) {
            setFeedback({ type: 'error', message: 'Veuillez saisir un numéro de téléphone' });
            return;
        }
        if (!formData.payment_method) {
            setFeedback({ type: 'error', message: 'Veuillez sélectionner un mode de paiement' });
            return;
        }
        if (!formData.forfait_id) {
            setFeedback({ type: 'error', message: 'Veuillez sélectionner un forfait de séance' });
            return;
        }
        if (selectedForfait?.with_coach && !formData.coach_id) {
            setFeedback({ type: 'error', message: 'Ce forfait comprend un suivi personnalisé : veuillez désigner le coach personnel' });
            return;
        }
        if (!formData.session_date) {
            setFeedback({ type: 'error', message: 'Veuillez indiquer la date de la séance' });
            return;
        }
        if (!formData.session_time) {
            setFeedback({ type: 'error', message: "Veuillez indiquer l'heure de la séance" });
            return;
        }

        try {
            setIsSubmitting(true);
            await api.post('/seances/enregistrer', {
                last_name: formData.last_name.trim(),
                first_name: formData.first_name.trim(),
                gender: formData.gender,
                date_of_birth: formData.date_of_birth || null,
                email: formData.email.trim() || null,
                phone: formData.phone.trim(),
                payment_method: formData.payment_method,
                forfait_id: Number(formData.forfait_id),
                coach_id: formData.coach_id ? Number(formData.coach_id) : null,
                session_date: formData.session_date,
                session_time: formData.session_time,
                include_inscription: formData.include_inscription
            });

            setFeedback({
                type: 'success',
                message: 'Séance enregistrée et payée avec succès !'
            });

            // Prepare receipt data
            setReceiptData({
                clientName: `${formData.first_name} ${formData.last_name}`,
                clientPhone: formData.phone,
                forfaitName: selectedForfait?.name || 'Séance',
                sessionDate: formData.session_date,
                sessionTime: formData.session_time,
                paymentMethod: PAYMENT_METHODS.find(m => m.value === formData.payment_method)?.label || formData.payment_method,
                total: totalPrice
            });
            setIsReceiptOpen(true);

            // Reset form
            resetForm();
            // Refresh clients and seances cache
            fetchClients();
            fetchSeances();
        } catch (err: any) {
            console.error('Erreur enregistrement séance:', err);
            setFeedback({
                type: 'error',
                message: err.response?.data?.error || "Erreur lors de l'enregistrement de la séance"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Toggle status (valide -> consomme)
    const handleUpdateStatus = async (id: number, newStatus: string) => {
        try {
            await api.put(`/seances/${id}/status`, { status: newStatus });
            setSeances(prev => prev.map(s => s.id === id ? { ...s, status: newStatus } : s));
        } catch (error) {
            console.error('Erreur mise à jour statut séance:', error);
            alert('Erreur lors du changement de statut');
        }
    };

    // Filtered sessions
    const displayedSeances = useMemo(() => {
        return seances.filter(s => {
            const matchesSearch = 
                !searchTerm ||
                s.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.phone?.includes(searchTerm) ||
                s.forfait_name?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = 
                statusFilter === 'all' || s.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [seances, searchTerm, statusFilter]);

    return (
        <div className="p-2.5 sm:p-4 md:p-6 bg-slate-50 min-h-screen">
            {/* Top Bar with Title, Breadcrumb and Tab Switcher */}
            <div className="max-w-5xl mx-auto mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-slate-700 tracking-tight flex items-center gap-2">
                        <span>Séances</span>
                        <span className="text-slate-400 font-light">/</span>
                        <span className="text-slate-500 font-medium">
                            {activeTab === 'ajout' ? 'Ajout' : 'Liste des séances'}
                        </span>
                    </h1>
                    <p className="text-xs text-slate-400 mt-0.5">
                        Enregistrement et gestion des séances à l'unité et pass journaliers
                    </p>
                </div>

                {/* Sub-tab navigation */}
                <div className="grid grid-cols-3 sm:flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 shadow-xs w-full sm:w-auto">
                    <button
                        type="button"
                        onClick={() => { setReaboClient(null); setIsReaboOpen(true); }}
                        className="flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-4 py-2 rounded-lg text-[11px] sm:text-xs font-bold transition-all text-amber-700 bg-amber-50 hover:bg-amber-100 text-center"
                    >
                        <RotateCw size={14} className="shrink-0" />
                        <span className="truncate">Réabonner</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSwitchTab('ajout')}
                        className={`flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-4 py-2 rounded-lg text-[11px] sm:text-xs font-bold transition-all text-center ${
                            activeTab === 'ajout'
                                ? 'bg-blue-600 text-white shadow-xs'
                                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                    >
                        <Plus size={14} className="shrink-0" />
                        <span>Ajout</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSwitchTab('liste')}
                        className={`flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-4 py-2 rounded-lg text-[11px] sm:text-xs font-bold transition-all text-center ${
                            activeTab === 'liste'
                                ? 'bg-blue-600 text-white shadow-xs'
                                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                    >
                        <List size={14} className="shrink-0" />
                        <span className="truncate">Liste ({seances.length})</span>
                    </button>
                </div>
            </div>

            {/* TAB 1: FORMULAIRE AJOUT EXACT DU SCREENSHOT 2 */}
            {activeTab === 'ajout' && (
                <div className="max-w-5xl mx-auto">
                    {feedback && (
                        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between shadow-xs ${
                            feedback.type === 'success' 
                                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                                : 'bg-red-50 text-red-800 border border-red-200'
                        }`}>
                            <div className="flex items-center gap-2 text-sm font-medium">
                                {feedback.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                                <span>{feedback.message}</span>
                            </div>
                            <button 
                                onClick={() => setFeedback(null)}
                                className="text-xs font-semibold underline hover:no-underline"
                            >
                                Fermer
                            </button>
                        </div>
                    )}

                    <div className="bg-white rounded-lg border border-gray-200/90 shadow-xs overflow-hidden">
                        {/* Header Box */}
                        <div className="px-6 py-4 border-b border-gray-100 bg-white">
                            <h2 className="text-base font-bold text-gray-800">
                                Entrez les informations suivantes
                            </h2>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="p-4 sm:p-6 md:p-8 space-y-5 sm:space-y-6">
                            {/* ROW 1: NOM & PRENOMS */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* NOM * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        NOM <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <User size={18} />
                                        </div>
                                        <input
                                            type="text"
                                            required
                                            value={formData.last_name}
                                            onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                                            placeholder="Entrez le nom"
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>

                                {/* PRENOMS * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        PRENOMS <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <User size={18} />
                                        </div>
                                        <input
                                            type="text"
                                            required
                                            value={formData.first_name}
                                            onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                                            placeholder="Entrez le(s) prénom(s)"
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ROW 2: GENRE & DATE DE NAISSANCE */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* GENRE * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        GENRE <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <span className="text-base leading-none">⚤</span>
                                        </div>
                                        <select
                                            required
                                            value={formData.gender}
                                            onChange={e => setFormData({ ...formData, gender: e.target.value })}
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        >
                                            <option value="">Sélectionner</option>
                                            <option value="Homme">Homme</option>
                                            <option value="Femme">Femme</option>
                                            <option value="Autre">Autre</option>
                                        </select>
                                    </div>
                                </div>

                                {/* DATE DE NAISSANCE */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        DATE DE NAISSANCE
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Calendar size={18} />
                                        </div>
                                        <input
                                            type="date"
                                            value={formData.date_of_birth}
                                            onChange={e => setFormData({ ...formData, date_of_birth: e.target.value })}
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ROW 3: EMAIL & TELEPHONE */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* EMAIL */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        EMAIL
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Mail size={18} />
                                        </div>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="Entrez l'adresse e-mail"
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>

                                {/* TELEPHONE * */}
                                <div className="relative">
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        TELEPHONE <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Phone size={18} />
                                        </div>
                                        <input
                                            type="tel"
                                            required
                                            value={formData.phone}
                                            onChange={e => handlePhoneChange(e.target.value)}
                                            onFocus={() => {
                                                if (filteredClients.length > 0) setShowClientSuggestions(true);
                                            }}
                                            placeholder="Entrez un numéro de téléphone"
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>

                                    {/* Auto-suggestions if existing client */}
                                    {showClientSuggestions && filteredClients.length > 0 && (
                                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1 max-h-48 overflow-y-auto">
                                            <div className="px-2 py-1 text-[10px] uppercase font-bold text-gray-400">
                                                Clients existants trouvés
                                            </div>
                                            {filteredClients.map(c => (
                                                <button
                                                    key={c.id}
                                                    type="button"
                                                    onClick={() => handleSelectClient(c)}
                                                    className="w-full text-left px-3 py-2 text-xs rounded-md hover:bg-blue-50 flex items-center justify-between transition-colors"
                                                >
                                                    <span className="font-semibold text-gray-800">
                                                        {c.first_name} {c.last_name}
                                                    </span>
                                                    <span className="text-gray-500 font-mono text-[11px]">
                                                        {c.phone}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ROW 4: MODE DE PAIEMENT & FORFAIT */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* MODE DE PAIEMENT * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        MODE DE PAIEMENT <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <DollarSign size={18} />
                                        </div>
                                        <select
                                            required
                                            value={formData.payment_method}
                                            onChange={e => setFormData({ ...formData, payment_method: e.target.value })}
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        >
                                            <option value="">Sélectionner</option>
                                            {PAYMENT_METHODS.map(m => (
                                                <option key={m.value} value={m.value}>{m.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* FORFAIT * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        FORFAIT <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Layers size={18} />
                                        </div>
                                        <select
                                            required
                                            value={formData.forfait_id}
                                            onChange={e => setFormData({ ...formData, forfait_id: e.target.value })}
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        >
                                            <option value="">Sélectionner</option>
                                            {forfaits.map(f => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name} ({Number(f.price).toLocaleString()} FCFA{Number(f.registration_fee) > 0 ? ` + Inscription: ${Number(f.registration_fee).toLocaleString()} F` : ''})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* SÉLECTION DU COACH PERSONNEL (SI LE FORFAIT SÉANCE EN REPOURVOIT UN) */}
                            {Boolean(selectedForfait?.with_coach) && (
                                <div className="p-4 bg-amber-50/80 border-2 border-amber-300 rounded-xl space-y-2 animate-in fade-in duration-150">
                                    <label className="block text-xs font-bold text-amber-900 uppercase tracking-wide flex items-center gap-1.5">
                                        <Dumbbell size={16} className="text-amber-600" />
                                        <span>Coach Personnel de la Séance <span className="text-red-500">*</span></span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <select
                                            required
                                            value={formData.coach_id}
                                            onChange={e => setFormData({ ...formData, coach_id: e.target.value })}
                                            className="w-full px-3.5 py-2.5 bg-white border border-amber-300 rounded-lg text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors shadow-2xs"
                                        >
                                            <option value="">-- Sélectionner le coach personnel pour cette séance --</option>
                                            {coaches.map(c => (
                                                <option key={c.id} value={c.id}>
                                                    {c.first_name} {c.last_name} {c.specialties ? `— Spécialité : ${c.specialties}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <p className="text-[11px] text-amber-800">
                                        Cette formule est une séance avec coach. Veuillez sélectionner le coach qui encadrera le client.
                                    </p>
                                </div>
                            )}

                            {/* ROW 5: DATE & HEURE */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* DATE * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        DATE <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Calendar size={18} />
                                        </div>
                                        <input
                                            type="date"
                                            required
                                            value={formData.session_date}
                                            onChange={e => setFormData({ ...formData, session_date: e.target.value })}
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>

                                {/* HEURE * */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                                        HEURE <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3.5 text-gray-400 pointer-events-none">
                                            <Clock size={18} />
                                        </div>
                                        <input
                                            type="time"
                                            required
                                            value={formData.session_time}
                                            onChange={e => setFormData({ ...formData, session_time: e.target.value })}
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* INDICATION FRAIS D'INSCRIPTION AUTOMATIQUES */}
                            {selectedForfait && Number(selectedForfait.registration_fee) > 0 ? (
                                <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></div>
                                        <span className="text-xs sm:text-sm font-semibold text-gray-800">
                                            Frais d'inscription obligatoire pour ce forfait : <strong className="text-amber-700 font-bold">+{Number(selectedForfait.registration_fee).toLocaleString()} FCFA</strong> (appliqués automatiquement)
                                        </span>
                                    </div>
                                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                                        Obligatoire
                                    </span>
                                </div>
                            ) : selectedForfait ? (
                                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between text-xs text-gray-600">
                                    <span>Aucun frais d'inscription requis pour ce forfait.</span>
                                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-200 text-gray-700">Sans inscription</span>
                                </div>
                            ) : null}

                            {/* RÉCAPITULATIF SÉANCE */}
                            {selectedForfait && (
                                <div className="p-4 bg-blue-50/60 border border-blue-100 rounded-lg flex flex-wrap items-center justify-between gap-3 text-xs text-blue-900">
                                    <div className="flex items-center gap-2">
                                        <Sparkles size={16} className="text-blue-600 shrink-0" />
                                        <span>
                                            Séance choisie : <strong className="font-bold">{selectedForfait.name}</strong>
                                            {Number(selectedForfait.registration_fee) > 0 && (
                                                <span className="text-amber-700 font-semibold ml-1">
                                                    + Frais d'inscription ({Number(selectedForfait.registration_fee).toLocaleString()} FCFA)
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span>
                                            Planifiée pour le <strong className="font-semibold">{formData.session_date}</strong> à <strong className="font-semibold">{formData.session_time}</strong>
                                        </span>
                                        <span className="bg-blue-600 text-white font-bold px-2.5 py-1 rounded-md text-xs">
                                            Montant : {totalPrice.toLocaleString()} FCFA
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* SUBMIT BUTTON - EXACT BLUE BUTTON FROM SCREENSHOT 2 */}
                            <div className="pt-4 flex justify-center">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-10 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm tracking-wide rounded-md shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none uppercase"
                                >
                                    {isSubmitting ? 'Enregistrement...' : 'SÉANCE'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* TAB 2: LISTE DES SÉANCES */}
            {activeTab === 'liste' && (
                <div className="max-w-5xl mx-auto space-y-4">
                    {/* Filters & Search */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200/80 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3">
                        <div className="relative w-full sm:w-80">
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                            <input
                                type="text"
                                placeholder="Rechercher par nom, téléphone, forfait..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>

                        <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto">
                            <span className="text-xs text-gray-500 font-medium">Statut :</span>
                            <div className="grid grid-cols-4 sm:flex bg-slate-100 p-0.5 rounded-md text-xs w-full sm:w-auto text-center">
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('all')}
                                    className={`px-3 py-1 rounded-sm font-semibold transition-colors ${
                                        statusFilter === 'all' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    Toutes
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('valide')}
                                    className={`px-3 py-1 rounded-sm font-semibold transition-colors ${
                                        statusFilter === 'valide' ? 'bg-white text-emerald-700 shadow-xs' : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    Valides
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('consomme')}
                                    className={`px-3 py-1 rounded-sm font-semibold transition-colors ${
                                        statusFilter === 'consomme' ? 'bg-white text-blue-700 shadow-xs' : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    Consommées
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('annule')}
                                    className={`px-3 py-1 rounded-sm font-semibold transition-colors ${
                                        statusFilter === 'annule' ? 'bg-white text-red-700 shadow-xs' : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    Annulées
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-lg border border-gray-200/80 shadow-xs overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs min-w-[700px]">
                                <thead className="bg-slate-50 text-slate-600 font-bold uppercase text-[11px] tracking-wider border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3">Client</th>
                                        <th className="px-4 py-3">Téléphone</th>
                                        <th className="px-4 py-3">Forfait Séance</th>
                                        <th className="px-4 py-3">Date & Heure</th>
                                        <th className="px-4 py-3">Montant</th>
                                        <th className="px-4 py-3">Paiement</th>
                                        <th className="px-4 py-3">Statut</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {loadingSeances ? (
                                        <tr>
                                            <td colSpan={8} className="text-center py-8 text-gray-500">
                                                Chargement des séances...
                                            </td>
                                        </tr>
                                    ) : displayedSeances.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="text-center py-8 text-gray-500">
                                                Aucune séance trouvée
                                            </td>
                                        </tr>
                                    ) : (
                                        displayedSeances.map((s: any) => (
                                            <tr key={s.id} className="hover:bg-slate-50/70 transition-colors">
                                                <td className="px-4 py-3 font-semibold text-gray-900">
                                                    {s.first_name} {s.last_name}
                                                </td>
                                                <td className="px-4 py-3 text-gray-600 font-mono">
                                                    {s.phone || '-'}
                                                </td>
                                                <td className="px-4 py-3 font-medium text-blue-700">
                                                    <div>{s.forfait_name || 'Séance'}</div>
                                                    {s.coach_name && (
                                                        <div className="text-[11px] font-bold text-amber-700 flex items-center gap-1 mt-0.5">
                                                            <span>🏋️ Coach :</span> <span>{s.coach_name}</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                                    {s.session_date ? new Date(s.session_date).toLocaleDateString('fr-FR') : '-'}
                                                    {' à '}
                                                    <span className="font-semibold text-gray-800">{s.session_time?.substring(0, 5)}</span>
                                                </td>
                                                <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">
                                                    {Number(s.amount || 0).toLocaleString()} FCFA
                                                </td>
                                                <td className="px-4 py-3 text-gray-600 uppercase text-[10px] font-semibold">
                                                    {s.payment_method || 'especes'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-1.5">
                                                        {s.status === 'valide' && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                                                                Valide
                                                            </span>
                                                        )}
                                                        {s.status === 'consomme' && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800">
                                                                Consommée
                                                            </span>
                                                        )}
                                                        {s.status === 'annule' && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-200 text-gray-700">
                                                                Annulée
                                                            </span>
                                                        )}
                                                        {Number(s.prolongations_count || 0) > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setEvolutionSeance(s)}
                                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 cursor-pointer transition-colors"
                                                                title="Voir l'historique des prolongations"
                                                            >
                                                                <History size={10} />
                                                                <span>Prolongée ({s.prolongations_count})</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {s.status === 'valide' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleUpdateStatus(s.id, 'consomme')}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200/80 rounded-lg font-bold transition-colors cursor-pointer"
                                                                title="Marquer comme consommée à l'accueil"
                                                            >
                                                                <CheckCheck size={13} />
                                                                <span>Pointer</span>
                                                            </button>
                                                        )}
                                                        {s.status === 'valide' ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => setProlongingSeance(s)}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200/80 rounded-lg font-bold transition-colors cursor-pointer"
                                                                title="Prolonger cette séance en cours"
                                                            >
                                                                <CalendarPlus size={13} />
                                                                <span>Prolonger</span>
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => { setReaboClient({ id: s.client_id, first_name: s.first_name, last_name: s.last_name, phone: s.phone }); setIsReaboOpen(true); }}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200/80 rounded-lg font-bold transition-colors cursor-pointer"
                                                                title="Réabonner ce client"
                                                            >
                                                                <RotateCw size={13} />
                                                                <span>Réabonner</span>
                                                            </button>
                                                        )}

                                                        <TableActionMenu
                                                            items={[
                                                                {
                                                                    label: 'Évolution',
                                                                    icon: History,
                                                                    onClick: () => setEvolutionSeance(s),
                                                                    badge: Number(s.prolongations_count || 0) > 0 ? s.prolongations_count : undefined,
                                                                    color: 'text-indigo-600'
                                                                },
                                                                {
                                                                    label: 'Badge',
                                                                    icon: BadgeCheck,
                                                                    onClick: () => setBadgeClient({ id: s.client_id, first_name: s.first_name, last_name: s.last_name, phone: s.phone, card_number: s.card_number, photo_url: s.photo_url }),
                                                                    color: 'text-amber-600'
                                                                },
                                                                {
                                                                    label: 'Imprimer reçu',
                                                                    icon: Printer,
                                                                    onClick: () => {
                                                                        setReceiptData({
                                                                            clientName: `${s.first_name} ${s.last_name}`,
                                                                            clientPhone: s.phone,
                                                                            forfaitName: s.forfait_name || 'Séance',
                                                                            sessionDate: s.session_date ? new Date(s.session_date).toLocaleDateString('fr-FR') : '',
                                                                            sessionTime: s.session_time?.substring(0, 5) || '',
                                                                            paymentMethod: s.payment_method || 'Espèces',
                                                                            total: Number(s.amount || 0)
                                                                        });
                                                                        setIsReceiptOpen(true);
                                                                    },
                                                                    color: 'text-blue-600'
                                                                }
                                                            ]}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <ClientBadgeModal isOpen={!!badgeClient} onClose={() => setBadgeClient(null)} client={badgeClient} />
            <ReabonnementModal
                isOpen={isReaboOpen}
                mode="seance"
                initialClient={reaboClient}
                onClose={() => setIsReaboOpen(false)}
                onOpenProlonger={(item) => setProlongingSeance(item)}
                onSuccess={(receipt) => {
                    setReceiptData(receipt);
                    setIsReceiptOpen(true);
                    fetchSeances();
                }}
            />
            {/* Modal de Prolongation de séance */}
            <ProlongerModal
                isOpen={!!prolongingSeance}
                type="seance"
                subscription={prolongingSeance}
                onClose={() => setProlongingSeance(null)}
                onSuccess={(receipt) => {
                    setReceiptData(receipt);
                    setIsReceiptOpen(true);
                    fetchSeances();
                    setFeedback({ type: 'success', message: 'Séance prolongée et enregistrée avec succès !' });
                }}
            />
            {/* Modal d'historique des évolutions & prolongations */}
            <EvolutionHistoryModal
                isOpen={!!evolutionSeance}
                type="seance"
                item={evolutionSeance}
                onClose={() => setEvolutionSeance(null)}
            />
            {/* Receipt Modal */}
            <ReceiptModal
                isOpen={isReceiptOpen}
                onClose={() => setIsReceiptOpen(false)}
                type="seance"
                data={receiptData || {}}
            />
        </div>
    );
};

export default Seances;
